import * as React from 'react'
import {Suspense, useContext, useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import LazyLoad from 'react-lazyload'
import {Link} from 'react-router-dom'
import {AppContext} from '../app'
import DataDriven from '../components/dataDriven'
import Events from '../components/events'
import Headquarters from '../components/headquarters'
import Partners from '../components/partners'
import Retention from '../components/retention'
import '../css/landing.css'
import {goToElementId, setTitleFont, tagger, TOPBAR_COLOR_ACCENT, TOPBAR_COLOR_AQUA, TOPBAR_COLOR_RED} from '../util/util'

const MainRtbAnimation = React.lazy(() => import('../lotties/main_rtb/mainRtb'))
const Monetization = React.lazy(() => import('../lotties/monetization/monetization'))
const MarketResearch = React.lazy(() => import('../lotties/market_research/marketResearch'))
const UserAppAnimation = React.lazy(() => import('../lotties/user_app/userApp'))

const Landing = () => {
    const appContext = useContext(AppContext)
    const {setTopbarColor} = appContext
    const accentText = 'accent-text'

    const {t, i18n} = useTranslation('landing')

    useEffect(() => {
        setTitleFont(i18n.language)
        
        const pdContainer = document.getElementById('pdContainer')
        const emContainer = document.getElementById('emContainer')

        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && entries[0].target === pdContainer) {
                setTopbarColor(TOPBAR_COLOR_AQUA)
            } else if (entries[0].isIntersecting && entries[0].target === emContainer) {
                setTopbarColor(TOPBAR_COLOR_RED)
            } else {
                setTopbarColor(TOPBAR_COLOR_ACCENT)
            }
        }, {threshold: [0.5]})

        pdContainer && observer.observe(pdContainer)
        emContainer && observer.observe(emContainer)

        return () => {
            pdContainer && observer.unobserve(pdContainer)
            emContainer && observer.unobserve(emContainer)
        }
    }, [])

    const scrollToHandler = () => goToElementId('pdContainer', true)

    return (
        <div className="landing">
            <div className="down-scroll not-tablet not-mobile" onClick={scrollToHandler}>
                <p className="down-scroll-text">SCROLL</p>
                <span className="down-scroll-animation down-scroll-animation-blue"> </span>
            </div>
            <div className="main-content">
                <div id="mlContainer">
                    <div className="title muli not-desktop not-mobile">{tagger(t('Machine Learning D'), accentText)}</div>
                    <div className="main-c-b">
                        <div className={'tablet main-rtb'}>
                            <div className="title muli not-tablet">{tagger(t('Machine Learning D'), accentText)}</div>
                            <div className="block-p">{t('Value first c')}</div>
                            <div className="block-c">{tagger(t('personaly creates'), accentText)}</div>
                            <div className="block-c">{t('we offer machine')}</div>
                            <div className="block-c trusted not-tablet not-mobile">{t('Trusted by')}</div>
                            <img className="not-tablet not-mobile" src="https://static.persona.ly/images/clients.svg" />
                        </div>
                        <div className="lottie-main-rtb-container tablet">
                            <Suspense fallback="">
                                <MainRtbAnimation />
                            </Suspense>
                        </div>
                    </div>
                    <div className="block-c trusted not-desktop supported">{t('Trusted by')}</div>
                    <img className="not-desktop trusted-rtb-img" src="https://static.persona.ly/images/clients.svg" ></img>
                </div>
                <div id="pdContainer">
                    <div>
                        <div className="title muli">{tagger(t('Parsing Data I'), 'aqua-text')}</div>
                        <div className="block-p">{t('Machine learning d')}</div>
                        <div className="block-c">{tagger(t('Personaly offers'), 'aqua-text fw700')}</div>
                        <div className="block-c">{t('While processing over')}</div>
                        <div className="block-p not-mobile not-tablet">{t('Our Data-driven P')}</div>
                        <DataDriven className="ddp-landing aqua-text not-mobile not-tablet" full={false} />
                        <Link to="rtb">
                            <button className="button-aqua large">{t('Learn More')}<img src="https://static.persona.ly/images/arrow.svg" />
                            </button>
                        </Link>
                    </div>
                    <LazyLoad className="lottie-user-app-container">
                        <Suspense fallback="">
                            <UserAppAnimation />
                        </Suspense>
                    </LazyLoad>
                </div>
                <div id="ddContainer">
                    <div className="title muli not-desktop not-mobile">{tagger(t('Data-Driven Sample P'), accentText)}</div>
                    <div className="main-c-b">
                        <div className={'tablet market-research-lg'}>
                            <div className="title muli not-tablet">{tagger(t('Data-Driven Sample P'), accentText)}</div>
                            <div className="block-p">{t('Quickly gain g')}</div>
                            <div
                                className="block-c">{tagger(t('Personaly provides m'), 'accent-text fw700')}</div>
                            <div className="block-c not-mobile not-tablet">{t('Using machine l')}</div>
                            <div className="sample-providers not-mobile not-tablet">
                                <span><img src="https://static.persona.ly/images/02.svg" /><p>{t('Feasibility & Cost')}</p></span>
                                <span><img src="https://static.persona.ly/images/03.svg" /><p>{t('Multi-Language S')}</p></span>
                                <span><img src="https://static.persona.ly/images/group-16.svg" /><p>{t('API Integration')}</p></span>
                            </div>
                            <Link to="market-research">
                                <button className="button-blue large">{t('Learn More')}<img src="https://static.persona.ly/images/arrow.svg" />
                                </button>
                            </Link>
                        </div>
                        <LazyLoad className="lottie-market-research-container tablet">
                            <Suspense fallback="">
                                <MarketResearch />
                            </Suspense>
                        </LazyLoad>
                    </div>
                </div>
                <div id="emContainer">
                    <div className="monetization-lg">
                        <div className="title muli">{tagger(t('Exceptional M'), 'red-text')}</div>
                        <div className="block-p">{t('Achieved by using a')}</div>
                        <div className="block-c">{tagger(t('Personaly provides a'), 'red-text fw700')}</div>
                        <Link to="monetization">
                            <button className="button-red large">{t('Learn More')}<img src="https://static.persona.ly/images/arrow.svg" />
                            </button>
                        </Link>
                    </div>
                    <LazyLoad className="lottie-monetization-container">
                        <Suspense fallback="">
                            <Monetization />
                        </Suspense>
                    </LazyLoad>
                </div>
            </div>
            <LazyLoad>
                <div className="partners-container">
                    <Partners />
                </div>
            </LazyLoad>
            <LazyLoad>
                <div className="blog-container">
                    <Retention />
                </div>
            </LazyLoad>
            <LazyLoad>
                <div className="event-container">
                    <Events />
                </div>
            </LazyLoad>
            <LazyLoad>
                <Headquarters showButton />
            </LazyLoad>
        </div>
    )
}

export default Landing



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/pages/landing.tsx